import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Search } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { EmptyState, Field, PageHeader, Pager, StatusBadge } from "@/components/shared/Common";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

export const CLIENT_FIELDS = [["rut", "RUT *"], ["razon_social", "Razón social *"], ["nombre_fantasia", "Nombre de fantasía"], ["giro", "Giro"], ["direccion", "Dirección"], ["comuna", "Comuna"], ["ciudad", "Ciudad"], ["telefono", "Teléfono"], ["email", "Email"]];
export const emptyClient = Object.fromEntries(CLIENT_FIELDS.map(([k]) => [k, ""]));

export const ClientForm = ({ value, onChange, disabled }) => (
  <div className="grid sm:grid-cols-2 gap-4">
    {CLIENT_FIELDS.map(([k, l]) => (
      <Field key={k} label={l}><Input data-testid={`client-${k}-input`} disabled={disabled} value={value[k] || ""} onChange={(e) => onChange({ ...value, [k]: e.target.value })} /></Field>
    ))}
  </div>
);

export default function Clients() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, page_size: 20 });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyClient);
  useEffect(() => { api.get("/gerencia/clients", { params: { q, page } }).then((r) => setData(r.data)); }, [q, page]);
  const canCreate = can("gerencia.clientes.gestionar") || can("gerencia.ventas.crear");

  const create = async (e) => {
    e.preventDefault();
    try { const { data: c } = await api.post("/gerencia/clients", form); toast.success("Cliente creado"); setForm(emptyClient); navigate(`/gerencia/clientes/${c.id}`); }
    catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div data-testid="clients-page">
      <PageHeader eyebrow="Gerencia" title="Clientes" subtitle="Cada cliente existe una sola vez (RUT único) y se relaciona con sus ventas y OT."
        actions={canCreate && <Button data-testid="create-client-btn" className="rounded-full" onClick={() => setOpen(true)}><Plus size={16} className="mr-1" />Nuevo cliente</Button>} />
      <div className="syc-card overflow-hidden">
        <div className="p-4 border-b border-[#e4e8e1] relative">
          <Search size={16} className="absolute left-7 top-1/2 -translate-y-1/2 text-[#8a9584]" />
          <Input data-testid="clients-search-input" className="pl-9" placeholder="Buscar por razón social o RUT" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} />
        </div>
        {data.items.length === 0 ? <EmptyState title="Sin clientes" text="Registre el primer cliente para comenzar a cotizar." /> : data.items.map((c) => (
          <Link key={c.id} to={`/gerencia/clientes/${c.id}`} data-testid={`client-row-${c.id}`} className="flex items-center gap-4 px-5 py-3 border-b border-[#f0f2ee] hover:bg-[#f6fbf2]">
            <div className="flex-1 min-w-0"><div className="font-semibold truncate">{c.razon_social}</div><div className="text-xs text-[#5b6656]">{c.rut} · {c.ciudad || c.comuna || "—"}</div></div>
            <StatusBadge status={c.status} label={c.status === "activo" ? "Activo" : "Inactivo"} />
          </Link>
        ))}
        <Pager page={page} total={data.total} pageSize={data.page_size} onPage={setPage} />
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="create-client-dialog">
          <DialogHeader><DialogTitle>Nuevo cliente</DialogTitle></DialogHeader>
          <form onSubmit={create} className="space-y-4">
            <ClientForm value={form} onChange={setForm} />
            <DialogFooter><Button data-testid="create-client-submit-btn" className="rounded-full">Crear cliente</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
